import { getSetting } from "../settings.js";
import { state } from "../state.js";

// ISO 3166-1 alpha-3 -> alpha-2.
const iso3ToAlpha2 = {
  AFG: "AF", ALA: "AX", ALB: "AL", DZA: "DZ", ASM: "AS", AND: "AD", AGO: "AO", AIA: "AI", ATA: "AQ", ATG: "AG",
  ARG: "AR", ARM: "AM", ABW: "AW", AUS: "AU", AUT: "AT", AZE: "AZ", BHS: "BS", BHR: "BH", BGD: "BD", BRB: "BB",
  BLR: "BY", BEL: "BE", BLZ: "BZ", BEN: "BJ", BMU: "BM", BTN: "BT", BOL: "BO", BIH: "BA", BWA: "BW", BVT: "BV",
  BRA: "BR", VGB: "VG", IOT: "IO", BRN: "BN", BGR: "BG", BFA: "BF", BDI: "BI", KHM: "KH", CMR: "CM", CAN: "CA",
  CPV: "CV", CYM: "KY", CAF: "CF", TCD: "TD", CHL: "CL", CHN: "CN", CXR: "CX", CCK: "CC", COL: "CO", COM: "KM",
  COG: "CG", COD: "CD", COK: "CK", CRI: "CR", CIV: "CI", HRV: "HR", CUB: "CU", CYP: "CY", CZE: "CZ", DNK: "DK",
  DJI: "DJ", DMA: "DM", DOM: "DO", ECU: "EC", EGY: "EG", SLV: "SV", GNQ: "GQ", ERI: "ER", EST: "EE", ETH: "ET",
  FLK: "FK", FRO: "FO", FJI: "FJ", FIN: "FI", FRA: "FR", GUF: "GF", PYF: "PF", ATF: "TF", GAB: "GA", GMB: "GM",
  GEO: "GE", DEU: "DE", GHA: "GH", GIB: "GI", GRC: "GR", GRL: "GL", GRD: "GD", GLP: "GP", GTM: "GT", GGY: "GG",
  GIN: "GN", GNB: "GW", GUY: "GY", HTI: "HT", HMD: "HM", VAT: "VA", HND: "HN", HKG: "HK", HUN: "HU", ISL: "IS",
  IND: "IN", IDN: "ID", IRN: "IR", IRQ: "IQ", IRL: "IE", IMN: "IM", ISR: "IL", ITA: "IT", JAM: "JM", JPN: "JP",
  JEY: "JE", JOR: "JO", KAZ: "KZ", KEN: "KE", KIR: "KI", PRK: "KP", KOR: "KR", KWT: "KW", KGZ: "KG", LAO: "LA",
  LVA: "LV", LBN: "LB", LSO: "LS", LBR: "LR", LBY: "LY", LIE: "LI", LTU: "LT", LUX: "LU", MAC: "MO", MKD: "MK",
  MDG: "MG", MWI: "MW", MYS: "MY", MDV: "MV", MLI: "ML", MLT: "MT", MHL: "MH", MTQ: "MQ", MRT: "MR", MUS: "MU",
  MYT: "YT", MEX: "MX", FSM: "FM", MDA: "MD", MCO: "MC", MNG: "MN", MNE: "ME", MSR: "MS", MAR: "MA", MOZ: "MZ",
  MMR: "MM", NAM: "NA", NRU: "NR", NPL: "NP", NLD: "NL", ANT: "AN", NCL: "NC", NZL: "NZ", NIC: "NI", NER: "NE",
  NGA: "NG", NIU: "NU", NFK: "NF", MNP: "MP", NOR: "NO", OMN: "OM", PAK: "PK", PLW: "PW", PSE: "PS", PAN: "PA",
  PNG: "PG", PRY: "PY", PER: "PE", PHL: "PH", PCN: "PN", POL: "PL", PRT: "PT", PRI: "PR", QAT: "QA", REU: "RE",
  ROU: "RO", RUS: "RU", RWA: "RW", BLM: "BL", SHN: "SH", KNA: "KN", LCA: "LC", MAF: "MF", SPM: "PM", VCT: "VC",
  WSM: "WS", SMR: "SM", STP: "ST", SAU: "SA", SEN: "SN", SRB: "RS", SYC: "SC", SLE: "SL", SGP: "SG", SXM: "SX",
  SVK: "SK", SVN: "SI", SLB: "SB", SOM: "SO", ZAF: "ZA", SGS: "GS", SSD: "SS", ESP: "ES", LKA: "LK", SDN: "SD",
  SUR: "SR", SJM: "SJ", SWZ: "SZ", SWE: "SE", CHE: "CH", SYR: "SY", TWN: "TW", TJK: "TJ", TZA: "TZ", THA: "TH",
  TLS: "TL", TGO: "TG", TKL: "TK", TON: "TO", TTO: "TT", TUN: "TN", TUR: "TR", TKM: "TM", TCA: "TC", TUV: "TV",
  UGA: "UG", UKR: "UA", ARE: "AE", GBR: "GB", USA: "US", URY: "UY", UZB: "UZ", VUT: "VU", VEN: "VE", VNM: "VN",
  WLF: "WF", ESH: "EH", YEM: "YE", ZMB: "ZM", ZWE: "ZW",
};

// IOC (motorsport nationality) three-letter codes -> alpha-2. Where a code also exists in the
// ISO table above with a different meaning (e.g. "ANT": ISO Netherlands Antilles vs. IOC
// Antigua), the IOC entry wins below, matching the pre-refactor lookup order.
const ioc3ToAlpha2 = {
  AFG: "AF", ALB: "AL", DZA: "DZ", ASA: "AS", AND: "AD", ANG: "AO", ANT: "AG", ARG: "AR", ARM: "AM", ARU: "AW",
  AUS: "AU", AUT: "AT", AZE: "AZ", BAH: "BS", BRN: "BH", BAN: "BD", BAR: "BB", BLR: "BY", BEL: "BE", BIZ: "BZ",
  BEN: "BJ", BHU: "BT", BOL: "BO", BIH: "BA", BOT: "BW", BRA: "BR", IVB: "VG", BRU: "BN", BUL: "BG", BUR: "BF",
  BDI: "BI", CAM: "KH", CMR: "CM", CAN: "CA", CPV: "CV", CAY: "KY", CAF: "CF", CHA: "TD", CHI: "CL", CHN: "CN",
  COL: "CO", COM: "KM", CGO: "CG", COD: "CD", COK: "CK", CRC: "CR", CIV: "CI", CRO: "HR", CUB: "CU", CYP: "CY",
  CZE: "CZ", DEN: "DK", DJI: "DJ", DMA: "DM", DOM: "DO", ECU: "EC", EGY: "EG", ESA: "SV", URU: "UY", ESP: "ES",
  EST: "EE", ETH: "ET", GAB: "GA", GAM: "GM", GEO: "GE", GER: "DE", GHA: "GH", GBR: "GB", GRE: "GR", GRN: "GD",
  GUA: "GT", GUI: "GN", GBS: "GW", GUY: "GY", HAI: "HT", HON: "HN", HKG: "HK", HUN: "HU", ISV: "VI", IND: "IN",
  INA: "ID", IRI: "IR", IRL: "IE", ISR: "IL", ITA: "IT", JAM: "JM", JPN: "JP", JOR: "JO", KAZ: "KZ",
  KEN: "KE", KIR: "KI", KOR: "KR", PRK: "KP", KUW: "KW", KGZ: "KG", LAO: "LA", LAT: "LV", LBA: "LY", LBR: "LR",
  LIE: "LI", LTU: "LT", LUX: "LU", MAC: "MO", MAD: "MG", MAW: "MW", MAS: "MY", MDV: "MV", MLI: "ML", MLT: "MT",
  MEX: "MX", MDA: "MD", MGT: "MN", MNE: "ME", MAR: "MA", MOZ: "MZ", MYA: "MM", NAM: "NA", NRU: "NR", NEP: "NP",
  NED: "NL", NGR: "NG", NCA: "NI", NIG: "NE", NOR: "NO", OMA: "OM", PAK: "PK", PLE: "PS", PAN: "PA", PAR: "PY",
  PHI: "PH", POL: "PL", POR: "PT", PUR: "PR", QAT: "QA", ROU: "RO", RSA: "ZA", RUS: "RU", RWA: "RW",
  SAM: "WS", SMR: "SM", SEN: "SN", SRB: "RS", SEY: "SC", SLE: "SL", SIN: "SG", SKN: "KN", SLO: "SI", SOL: "SB",
  SOM: "SO", SUI: "CH", SUR: "SR", SWZ: "SZ", SWE: "SE", SYR: "SY", TPE: "TW", THA: "TH",
  TJK: "TJ", TKM: "TM", TLS: "TL", TOG: "TG", TGA: "TO", TTO: "TT", TUN: "TN", TUR: "TR", TUV: "TV", UGA: "UG",
  UKR: "UA", UAE: "AE", USA: "US", UZA: "UZ", VAN: "VU", VEN: "VE", VIE: "VN", YEM: "YE",
  ZAM: "ZM", ZIM: "ZW",
};

const countryMap = { ...iso3ToAlpha2, ...ioc3ToAlpha2 };

function toAlpha2(code) {
  if (!code) return "";
  const upper = code.toUpperCase();
  if (upper.length === 2) return upper;
  return (countryMap[upper] || "").toLowerCase();
}

export function getFlag(nat) {
  nat = (nat || "").trim();
  if (!getSetting("showNat") || nat === "") return "";
  return `<img src="${state.configJSON.ImageUrl}NAT/${toAlpha2(nat)}.png" alt="(${nat})" class="flag"> `;
}
